<template>
  <div id="analytics-map" ref="analyticsMapContainer" class="analytics-map-container">
    <!-- Left data panel -->
    <div class="analytics-left-panel">
      <!-- Data catalog section -->
      <div class="data-catalog-section">
        <h3>Data Catalog</h3>
        <div class="catalog-tabs">          <!-- Tab navigation -->
          <div class="tab-nav">
            <button 
              @click="activeTab = 'routes'" 
              :class="{ active: activeTab === 'routes' }"
            >Routes</button>
            <button 
              @click="activeTab = 'locations'" 
              :class="{ active: activeTab === 'locations' }"
            >Locations</button>
            <button 
              @click="activeTab = 'orders'" 
              :class="{ active: activeTab === 'orders' }"
            >Order Points</button>
            <button 
              @click="activeTab = 'analytics'" 
              :class="{ active: activeTab === 'analytics' }"
            >Retail Locations</button>
          </div>
          
          <!-- Tab content -->
          <div class="tab-content">
            <!-- Routes tab -->
            <div v-if="activeTab === 'routes'" class="tab-pane">
              <div v-if="isLoadingCatalogData" class="loading-message">Loading routes...</div>
              <div v-else-if="catalogData.routes.length === 0" class="empty-message">
                No saved routes available.
              </div>
              <ul v-else class="data-list">
                <li v-for="route in catalogData.routes" :key="route.id" 
                    :class="{ selected: selectedData.id === route.id && selectedData.type === 'route' }"
                    @click="selectDataItem(route, 'route')">
                  <span class="item-name">{{ route.name }}</span>
                  <span class="item-date">{{ route.date }}</span>
                </li>
              </ul>
            </div>
            
            <!-- Locations tab -->
            <div v-if="activeTab === 'locations'" class="tab-pane">
              <div v-if="isLoadingCatalogData" class="loading-message">Loading locations...</div>
              <div v-else-if="catalogData.locations.length === 0" class="empty-message">
                No locations available.
              </div>
              <ul v-else class="data-list">
                <li v-for="location in catalogData.locations" :key="location.id"
                    :class="{ selected: selectedData.id === location.id && selectedData.type === 'location' }"
                    @click="selectDataItem(location, 'location')">
                  <span class="item-name">{{ location.name }}</span>
                  <span class="item-type">{{ location.type }}</span>
                </li>
              </ul>
            </div>
              <!-- Order points tab -->
            <div v-if="activeTab === 'orders'" class="tab-pane">
              <div v-if="isLoadingCatalogData" class="loading-message">Loading order points...</div>
              <div v-else-if="catalogData.orders.length === 0" class="empty-message">
                No order points available.
              </div>
              <div v-else>                <!-- Orders analysis controls -->
                <div class="orders-analysis-controls">
                  <h4>Hexagon Analysis</h4>
                  
                  <div class="analysis-control">
                    <label>Column to Analyze:</label>
                    <select v-model="ordersAnalysis.selectedColumn" @change="updateOrdersVisualization">
                      <option v-for="column in ordersAnalysis.availableColumns" :key="column.name" :value="column.name">
                        {{ column.label }}
                      </option>
                    </select>
                  </div>
                  
                  <div class="analysis-control">
                    <label>Aggregation Method:</label>
                    <select v-model="ordersAnalysis.selectedAggregation" @change="updateOrdersVisualization">
                      <option v-for="agg in ordersAnalysis.availableAggregations" :key="agg.name" :value="agg.name">
                        {{ agg.label }}
                      </option>
                    </select>
                  </div>
                    <div class="analysis-control">
                    <label>Hexagon Size:</label>
                    <select v-model="ordersAnalysis.selectedHexagonSize" @change="updateOrdersVisualization">
                      <option v-for="size in ordersAnalysis.availableHexagonSizes" :key="size.value" :value="size.value">
                        {{ size.label }} - {{ size.description }}
                      </option>
                    </select>
                  </div>

                  <div class="analysis-control">
                    <label>Color Palette:</label>
                    <select v-model="ordersAnalysis.selectedColorPalette" @change="updateOrdersVisualization">
                      <option v-for="palette in ordersAnalysis.availableColorPalettes" :key="palette.name" :value="palette.name">
                        {{ palette.label }}
                      </option>
                    </select>
                  </div>

                  <div class="analysis-control">
                    <label>Distribution Method:</label>
                    <select v-model="ordersAnalysis.selectedDistribution" @change="updateOrdersVisualization">
                      <option v-for="distribution in ordersAnalysis.availableDistributions" :key="distribution.name" :value="distribution.name">
                        {{ distribution.label }}
                      </option>
                    </select>
                  </div>                  <div class="analysis-control">
                    <label>Number of Groups: {{ ordersAnalysis.numberOfGroups }}</label>
                    <input 
                      type="range" 
                      v-model.number="ordersAnalysis.numberOfGroups" 
                      min="3" 
                      max="10" 
                      step="1"
                      @change="updateOrdersVisualization"
                      class="range-slider"
                    />
                    <div class="range-labels">
                      <span>3</span>
                      <span>10</span>
                    </div>
                  </div>

                  <div class="analysis-control">
                    <label>Visualization Type:</label>
                    <select v-model="ordersAnalysis.selectedViewType" @change="updateOrdersVisualization">
                      <option v-for="viewType in ordersAnalysis.availableViewTypes" :key="viewType.name" :value="viewType.name">
                        {{ viewType.label }}
                      </option>
                    </select>
                  </div>

                  <div v-if="ordersAnalysis.selectedViewType === 'heatmap'" class="heatmap-config">
                    <div class="analysis-control">
                      <label>Heatmap Intensity: {{ ordersAnalysis.heatmapSettings.intensity }}</label>
                      <input 
                        type="range" 
                        v-model.number="ordersAnalysis.heatmapSettings.intensity" 
                        min="0" 
                        max="1" 
                        step="0.1"
                        @change="updateOrdersVisualization"
                        class="range-slider"
                      />
                    </div>
                    
                    <div class="analysis-control">
                      <label>Heatmap Radius: {{ ordersAnalysis.heatmapSettings.radius }}px</label>
                      <input 
                        type="range" 
                        v-model.number="ordersAnalysis.heatmapSettings.radius" 
                        min="5" 
                        max="30" 
                        step="1"
                        @change="updateOrdersVisualization"
                        class="range-slider"
                      />
                    </div>
                  </div>
                  
                  <button class="apply-analysis-btn" @click="applyOrdersAnalysis" :disabled="isLoadingOrdersAnalysis">
                    {{ isLoadingOrdersAnalysis ? 'Loading...' : 'Apply Analysis' }}
                  </button>
                    <div v-if="ordersAnalysis.results" class="analysis-summary">
                    <p><strong>{{ ordersAnalysis.results.total_hexagons || ordersAnalysis.results.total_orders || catalogData.orders.length }}</strong> 
                    {{ ordersAnalysis.selectedViewType === 'hexagon' ? 'hexagons' : 'orders' }} analyzed</p>
                    <p>Showing <strong>{{ ordersAnalysis.selectedAggregation }}</strong> of <strong>{{ getColumnLabel(ordersAnalysis.selectedColumn) }}</strong></p>
                    <p v-if="ordersAnalysis.selectedViewType === 'hexagon'">Resolution: <strong>H3 Level {{ ordersAnalysis.selectedHexagonSize }}</strong></p>
                    <p>View: <strong>{{ getViewTypeLabel(ordersAnalysis.selectedViewType) }}</strong></p>
                  </div>
                </div>
                  <!-- Orders list (simplified) -->
                <div class="orders-summary">                  <p><strong>{{ catalogData.orders.length }}</strong> total orders in database</p>
                  <p>Click "Apply Analysis" to visualize data</p>
                </div>
              </div>
            </div>
            
            <!-- Analytics locations tab (Retail locations) -->
            <div v-if="activeTab === 'analytics'" class="tab-pane">
              <div v-if="isLoadingCatalogData" class="loading-message">Loading retail locations...</div>
              <div v-else-if="catalogData.analyticsLocations.length === 0" class="empty-message">
                No retail locations available.
              </div>
              <div v-else>                <!-- Category filter -->
                <div class="analytics-category-filter">
                  <label>Filter by Category:</label>
                  <select v-model="analyticsFilter.selectedCategory" @change="filterAnalyticsLocations">
                    <option value="">All Categories</option>
                    <option v-for="category in analyticsFilter.availableCategories" :key="category._id" :value="category._id">
                      {{ category._id }} ({{ category.count }})
                    </option>
                  </select>
                </div>
                
                <!-- Visualization controls -->
                <div class="analytics-controls-panel">
                  <h4>Visualization Options</h4>
                  
                  <div class="analytics-control">
                    <label>
                      <input type="checkbox" v-model="analyticsVisualization.showPoints" @change="updateAnalyticsVisualization">
                      Show Location Points
                    </label>
                  </div>
                  
                  <div class="analytics-control">
                    <label>
                      <input type="checkbox" v-model="analyticsVisualization.showHexagons" @change="updateAnalyticsVisualization">
                      Show H3 Hexagons
                    </label>
                  </div>
                  
                  <div class="analytics-control" v-if="analyticsVisualization.showHexagons">
                    <label>Hexagon Resolution:</label>
                    <select v-model="analyticsVisualization.hexagonResolution" @change="updateAnalyticsVisualization">
                      <option value="7">Large (7) - ~5.2 km²</option>
                      <option value="8">Medium (8) - ~0.74 km²</option>
                      <option value="9">Small (9) - ~0.11 km²</option>
                    </select>
                  </div>
                  
                  <button class="apply-analysis-btn" @click="loadAnalyticsLocationsToMap" :disabled="isLoadingAnalyticsVisualization">
                    {{ isLoadingAnalyticsVisualization ? 'Loading...' : 'Show on Map' }}
                  </button>
                </div>
                
                <!-- Statistics summary -->
                <div v-if="analyticsStats" class="analytics-stats-summary">
                  <h4>Statistics</h4>
                  <p><strong>{{ analyticsStats.total_count }}</strong> total locations</p>
                  <p><strong>{{ filteredAnalyticsLocations.length }}</strong> filtered locations</p>
                  <div v-if="analyticsStats.category_distribution.length > 0" class="category-breakdown">
                    <p><strong>Top Categories:</strong></p>
                    <div v-for="cat in analyticsStats.category_distribution.slice(0, 5)" :key="cat._id" class="category-item">
                      <span>{{ cat._id }}: {{ cat.count }}</span>
                    </div>
                  </div>
                </div>
                
                <!-- Locations list (scrollable) -->
                <div class="analytics-locations-list">
                  <ul class="data-list">
                    <li v-for="location in filteredAnalyticsLocations.slice(0, 50)" :key="location.id"
                        :class="{ selected: selectedData.id === location.id && selectedData.type === 'analytics' }"
                        @click="selectDataItem(location, 'analytics')">
                      <span class="item-name">{{ location.name }}</span>
                      <span class="item-type">{{ location.category }}</span>
                      <span class="item-status">{{ location.type }}</span>
                    </li>
                  </ul>
                  <div v-if="filteredAnalyticsLocations.length > 50" class="load-more-info">
                    Showing first 50 of {{ filteredAnalyticsLocations.length }} locations
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>      </div>
    </div>
    
    <!-- Right layers panel -->
    <div class="analytics-right-panel">
      <h3>Map Layers</h3>
      <div class="layers-list">
        <div 
          v-for="(layer, key) in layers" 
          :key="key" 
          class="layer-item"
        >
          <div class="layer-name">{{ layer.label }}</div>
          <div 
            class="layer-visibility-toggle" 
            @click="toggleLayerVisibility(key)"
            :class="{ 'visible': layer.visible }"
          >
            <i class="eye-icon"></i>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Existing loading overlay -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-spinner"></div>
      <div class="loading-text">Loading analytics data...</div>
    </div>
    
    <!-- Existing analytics controls (adjusted position) -->
    <div class="analytics-controls">
      <h3>RouteBox Analytics</h3>
      <div class="analytics-filters">
        <div class="filter-group">
          <label>Date Range</label>
          <select v-model="dateRange">
            <option value="day">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="quarter">This Quarter</option>
          </select>
        </div>
        <div class="filter-group">
          <label>Vehicle Filter</label>
          <select v-model="vehicleFilter">
            <option value="all">All Vehicles</option>
            <option value="1">Vehicle 1</option>
            <option value="2">Vehicle 2</option>
            <option value="3">Vehicle 3</option>
          </select>
        </div>
        <button class="apply-btn" @click="applyFilters">Apply Filters</button>
      </div>
      <div class="analytics-stats">
        <div class="stat-box">
          <div class="stat-title">Total Distance</div>
          <div class="stat-value">{{ totalDistance }} km</div>
        </div>
        <div class="stat-box">
          <div class="stat-title">Total Deliveries</div>
          <div class="stat-value">{{ totalDeliveries }}</div>
        </div>
        <div class="stat-box">
          <div class="stat-title">Avg Time</div>
          <div class="stat-value">{{ avgTime }} min</div>
        </div>
        <div class="stat-box">
          <div class="stat-title">Efficiency</div>
          <div class="stat-value">{{ efficiency }}%</div>
        </div>
      </div>
    </div>
    <div class="back-button" @click="goBack">← Back to Planning</div>
  </div>
</template>

<script>
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { cellToBoundary } from 'h3-js';
import AnalyticsService from '../services/AnalyticsService';
import LocationsService from '../services/LocationsService';

export default {
  name: 'AnalyticsComponent',
    data() {
    return {
      map: null,
      accessToken: process.env.VUE_APP_MAPBOX_ACCESS_TOKEN,
      dateRange: 'week',
      vehicleFilter: 'all',
      totalDistance: '0',
      totalDeliveries: '0',
      avgTime: '0',
      efficiency: '0',
      heatmapLayer: null,
      routeHistoryLayers: [],
      istanbulGeoData: null,
      isLoading: true,
        // Map layers visibility control using Mapbox layer IDs
      layers: {
        istanbulBoundary: {
          ids: ['istanbul-boundary-fill', 'istanbul-boundary-line'],
          label: 'Istanbul Boundary', visible: true
        },
        heatmap: {
          ids: ['delivery-heatmap-layer'],
          label: 'Delivery Heatmap', visible: false
        },
        routes: {
          ids: ['route-history-layer', 'depot-points-layer', 'delivery-points-layer'],
          label: 'Route History', visible: false
        },
        ordersAnalysis: {
          ids: ['orders-hexagons-fill', 'orders-hexagons-stroke', 'orders-heatmap-layer', 'orders-points-layer'],
          label: 'Orders Analysis', visible: false
        },
        analyticsLocations: {
          ids: ['analytics-locations-points', 'analytics-locations-hexagons-fill', 'analytics-locations-hexagons-stroke'],
          label: 'Retail Locations', visible: false
        }
      },
      
      // New data properties for the left panel
      activeTab: 'routes',
      isLoadingCatalogData: false,
      selectedData: {
        id: null,
        type: null,
        data: null
      },      catalogData: {
        routes: [],
        locations: [],
        orders: [],
        analyticsLocations: []
      },
      
      // Analytics locations configuration
      analyticsFilter: {
        selectedCategory: '',
        availableCategories: []
      },
      
      analyticsVisualization: {
        showPoints: true,
        showHexagons: false,
        hexagonResolution: 8
      },
      
      analyticsStats: null,
      filteredAnalyticsLocations: [],
      isLoadingAnalyticsVisualization: false,// All view settings are now handled within the ordersAnalysis object
        // Orders analysis configuration
      ordersAnalysis: {
        selectedColumn: 'delivery_cost',
        selectedAggregation: 'sum',
        selectedHexagonSize: 8, // H3 resolution level (default: medium size)
        selectedViewType: 'hexagon', // Default view type
        availableViewTypes: [
          { name: 'hexagon', label: 'Hexagon View' },
          { name: 'heatmap', label: 'Heatmap View' },
          { name: 'points', label: 'Point View' }
        ],
        heatmapSettings: {
          intensity: 0.8,
          radius: 15
        },        availableColumns: [
          { name: 'delivery_cost', label: 'Delivery Cost' },
          { name: 'delivery_lmd_cost', label: 'Delivery LMD Cost' },
          { name: 'cargo_volume', label: 'Cargo Volume' },
          { name: 'cargo_dimension', label: 'Cargo Dimension' },
          { name: 'cargo_weight', label: 'Cargo Weight' },
          { name: 'cost_per_order', label: 'Cost per Order' }
        ],
        availableAggregations: [
          { name: 'sum', label: 'Sum' },
          { name: 'avg', label: 'Average' },
          { name: 'count', label: 'Count' },
          { name: 'min', label: 'Minimum' },
          { name: 'max', label: 'Maximum' }
        ],        availableHexagonSizes: [
          { value: 6, label: 'Very Large (6)', description: '~36 km²' },
          { value: 7, label: 'Large (7)', description: '~5.2 km²' },
          { value: 8, label: 'Medium (8)', description: '~0.74 km²' },
          { value: 9, label: 'Small (9)', description: '~0.11 km²' },
          { value: 10, label: 'Very Small (10)', description: '~0.015 km²' }
        ],
        selectedColorPalette: 'greenYellowRed',
        availableColorPalettes: [
          { 
            name: 'greenYellowRed', 
            label: 'Green → Yellow → Red',
            colors: ['#22c55e', '#84cc16', '#eab308', '#f59e0b', '#dc2626']
          },
          { 
            name: 'blueGreen', 
            label: 'Blue → Green',
            colors: ['#dbeafe', '#93c5fd', '#60a5fa', '#3b82f6', '#1d4ed8']
          },
          { 
            name: 'redOrange', 
            label: 'Red → Orange',
            colors: ['#fef2f2', '#fecaca', '#f87171', '#ef4444', '#dc2626']
          },
          { 
            name: 'purple', 
            label: 'Purple Gradient',
            colors: ['#f3e8ff', '#c4b5fd', '#a78bfa', '#8b5cf6', '#7c3aed']
          },
          { 
            name: 'thermal', 
            label: 'Thermal',
            colors: ['#000004', '#420a68', '#932667', '#dd513a', '#fca50a']
          }
        ],
        selectedDistribution: 'linear',
        availableDistributions: [
          { name: 'linear', label: 'Linear Distribution' },
          { name: 'quantile', label: 'Quantile Distribution' },
          { name: 'logarithmic', label: 'Logarithmic Distribution' },
          { name: 'exponential', label: 'Exponential Distribution' }
        ],
        numberOfGroups: 5,
        results: null
      },
      isLoadingOrdersAnalysis: false
    };
  },
    mounted() {
    this.initializeMap();
    this.loadCatalogData();
    this.loadOrdersConfiguration();
  },
  
  methods: {
    // Existing methods
    initializeMap() {
      mapboxgl.accessToken = this.accessToken;
      this.map = new mapboxgl.Map({
        container: this.$refs.analyticsMapContainer,
        style: 'mapbox://styles/mapbox/light-v11',
        center: [29.0, 41.0], // Istanbul coordinates
        zoom: 9
      });
      // hide loading overlay as soon as the map is ready
      this.map.on('load', () => {
        this.isLoading = false;
      });
    },
    
    fetchHeatmapData() {
      AnalyticsService.getHeatmapData()
        .then(response => {
          this.addHeatmapLayer(response.data);
        })
        .catch(error => {
          console.error('Error fetching heatmap data:', error);
        });
    },
    
    fetchRouteHistoryData() {
      AnalyticsService.getRouteData()
        .then(response => {        this.addRouteHistoryLayer(response.data);
          // Calculate analytics after all data is loaded
          this.calculateAnalytics();
        // Mark routes layer visible in panel
        this.updateLayerStateAfterDataLoad('routes', true);
          this.isLoading = false;
        })
        .catch(error => {
          console.error('Error fetching route data:', error);
          this.isLoading = false;
        });
    },
      addIstanbulBoundary() {
      if (!this.istanbulGeoData) {
        console.error("Istanbul GeoData not loaded");
        return;
      }
      
      // Add Istanbul GeoJSON boundary data
      this.map.addSource('istanbul-boundary', {
        'type': 'geojson',
        'data': this.istanbulGeoData
      });
        // Add a fill layer for the Istanbul boundary
      this.map.addLayer({
        'id': 'istanbul-boundary-fill',
        'type': 'fill',
        'source': 'istanbul-boundary',
        'layout': {
          'visibility': this.layers.istanbulBoundary.visible ? 'visible' : 'none'
        },
        'paint': {
          'fill-color': '#f8f9fa',
          'fill-opacity': 0.2
        }
      });
        // Add an outline layer for the Istanbul boundary
      this.map.addLayer({
        'id': 'istanbul-boundary-line',
        'type': 'line',
        'source': 'istanbul-boundary',
        'layout': {
          'visibility': this.layers.istanbulBoundary.visible ? 'visible' : 'none'
        },
        'paint': {
          'line-color': '#2c3e50',
          'line-width': 1,
          'line-opacity': 0.8
        }
      });
      
      // Fit map to Istanbul boundary
      const bounds = new mapboxgl.LngLatBounds();
      
      // Get the coordinates from the first feature's geometry (Istanbul multipolygon)
      if (this.istanbulGeoData.features && this.istanbulGeoData.features.length > 0) {
        const geometry = this.istanbulGeoData.features[0].geometry;
        
        if (geometry.type === 'MultiPolygon' && geometry.coordinates.length > 0) {
          // For MultiPolygon, we need to iterate through all polygons
          geometry.coordinates.forEach(polygon => {
            polygon.forEach(ring => {
              ring.forEach(coord => {
                bounds.extend(coord);
              });
            });
          });
        }
      }
      
      // Fit the map to the bounds with some padding
      this.map.fitBounds(bounds, { padding: 40 });
    },      addHeatmapLayer(heatmapData) {
      // Check if there's valid data to display
      if (!heatmapData || !heatmapData.features || heatmapData.features.length === 0) {
        console.log("No heatmap data available from database");
        return;
      }

      // Add heatmap layer with data from API
      this.map.addSource('delivery-heatmap', {
        'type': 'geojson',
        'data': heatmapData
      });
        this.map.addLayer({
        id: 'delivery-heatmap-layer',
        type: 'heatmap',
        source: 'delivery-heatmap',
        layout: { 
          visibility: this.layers.heatmap.visible ? 'visible' : 'none' 
        },
        paint: {
          'heatmap-weight': ['get', 'weight'],
          'heatmap-intensity': 0.8,
          'heatmap-color': [
            'interpolate',
            ['linear'],
            ['heatmap-density'],
            0, 'rgba(33,102,172,0)',
            0.2, 'rgb(103,169,207)',
            0.4, 'rgb(209,229,240)',
            0.6, 'rgb(253,219,199)',
            0.8, 'rgb(239,138,98)',
            1, 'rgb(178,24,43)'
          ],
          'heatmap-radius': 15,
          'heatmap-opacity': 0.7
        }
      });
        this.heatmapLayer = 'delivery-heatmap-layer';
      // Mark heatmap layer visible in panel
      this.updateLayerStateAfterDataLoad('heatmap', true);
    },      addRouteHistoryLayer(routeData) {
      // Check if there's valid data to display
      if (!routeData || !routeData.features || routeData.features.length === 0) {
        console.log("No route data available from database");
        return;
      }

      // Add route history layer with data from API
      this.map.addSource('route-history', {
        'type': 'geojson',
        'data': routeData
      });
        // Add route lines
      this.map.addLayer({
        'id': 'route-history-layer',
        'type': 'line',
        'source': 'route-history',
        'layout': {
          'line-join': 'round',
          'line-cap': 'round',
          'visibility': this.layers.routes.visible ? 'visible' : 'none'
        },
        'paint': {
          'line-color': [
            'match',
            ['get', 'vehicle'],
            '1', '#ff6b6b',
            '2', '#4ecdc4',
            '3', '#1a535c',
            '#6c757d'  // default color
          ],
          'line-width': 4,
          'line-opacity': 0.8
        }
      });
      
      this.routeHistoryLayers.push('route-history-layer');
      
      // Extract all points from route data to show as markers
      const pointFeatures = [];
      let depotPoints = new Set(); // Use a Set to avoid duplicate depot points
      
      // Process each feature to extract points
      if (routeData && routeData.features) {
        routeData.features.forEach(feature => {
          if (feature.geometry && feature.geometry.type === 'LineString' && feature.geometry.coordinates) {
            const coords = feature.geometry.coordinates;
            const vehicle = feature.properties?.vehicle || '';
            
            // Add first point as depot (only once per vehicle)
            if (coords.length > 0) {
              const depotKey = coords[0].join(','); // Create a string key from coordinates
              if (!depotPoints.has(depotKey)) {
                depotPoints.add(depotKey);
                pointFeatures.push({
                  type: 'Feature',
                  properties: { 
                    type: 'depot',
                    vehicle: vehicle
                  },
                  geometry: {
                    type: 'Point',
                    coordinates: coords[0]
                  }
                });
              }
              
              // Add delivery points (skip first and last if they're the same - depot)
              const isCircular = coords.length > 1 && 
                coords[0][0] === coords[coords.length-1][0] && 
                coords[0][1] === coords[coords.length-1][1];
              
              // Add all points except depot duplicates
              for (let i = 1; i < coords.length - (isCircular ? 1 : 0); i++) {
                pointFeatures.push({
                  type: 'Feature',
                  properties: { 
                    type: 'delivery',
                    vehicle: vehicle
                  },
                  geometry: {
                    type: 'Point',
                    coordinates: coords[i]
                  }
                });
              }
            }
          }
        });
      }
      
      // Add source for points
      this.map.addSource('route-points', {
        'type': 'geojson',
        'data': {
          'type': 'FeatureCollection',
          'features': pointFeatures
        }
      });
        // Add layer for depot points
      this.map.addLayer({
        'id': 'depot-points-layer',
        'type': 'circle',
        'source': 'route-points',
        'filter': ['==', ['get', 'type'], 'depot'],
        'layout': {
          'visibility': this.layers.routes.visible ? 'visible' : 'none'
        },
        'paint': {
          'circle-radius': 10,
          'circle-color': '#ff0000',
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff'
        }
      });
        // Add layer for delivery points
      this.map.addLayer({
        'id': 'delivery-points-layer',
        'type': 'circle',
        'source': 'route-points',
        'filter': ['==', ['get', 'type'], 'delivery'],
        'layout': {
          'visibility': this.layers.routes.visible ? 'visible' : 'none'
        },
        'paint': {
          'circle-radius': 6,
          'circle-color': [
            'match',
            ['get', 'vehicle'],
            '1', '#ff6b6b',
            '2', '#4ecdc4',
            '3', '#1a535c',
            '#6c757d'  // default color
          ],
          'circle-stroke-width': 1,
          'circle-stroke-color': '#ffffff'
        }
      });
      
      this.routeHistoryLayers.push('depot-points-layer');
      this.routeHistoryLayers.push('delivery-points-layer');
    },
    
    calculateAnalytics() {
      // Calculate analytics based on the route data
      let totalDist = 0;
      let totalDeliv = 0;
      let routeCount = 0;
      
      // Get route source data
      const routeSource = this.map.getSource('route-history');
      if (routeSource && routeSource._data && routeSource._data.features) {
        const features = routeSource._data.features;
        routeCount = features.length;
        
        // Sum up all distances and deliveries
        features.forEach(feature => {
          if (feature.properties) {
            totalDist += parseFloat(feature.properties.distance || 0);
            totalDeliv += parseInt(feature.properties.deliveries || 0);
          }
        });
      }
      
      // Update the stats
      this.totalDistance = totalDist.toFixed(1);
      this.totalDeliveries = totalDeliv.toString();
      this.avgTime = (totalDist / routeCount * 1.2).toFixed(1); // Rough estimate: 1.2 minutes per km
      this.efficiency = Math.round(90 + Math.random() * 10).toString(); // Random efficiency between 90-100%
    },
      clearRouteHistoryLayers() {
      // Remove all route history layers
      this.routeHistoryLayers.forEach(layerId => {
        if (this.map.getLayer(layerId)) {
          this.map.removeLayer(layerId);
        }
      });
      
      // Clear the array of layer IDs
      this.routeHistoryLayers = [];
      
      // Remove sources if they exist
      if (this.map.getSource('route-history')) {
        this.map.removeSource('route-history');
      }
      
      if (this.map.getSource('route-points')) {
        this.map.removeSource('route-points');
      }
      
      // Reset layer state in panel
      this.resetLayerState('routes');
    },
      applyFilters() {
      console.log('Applying filters:', this.dateRange, this.vehicleFilter);
      this.isLoading = true;
      
      // Clear existing route history layers and sources
      this.clearRouteHistoryLayers();
      
      // Get the full route data again
      AnalyticsService.getRouteData()
        .then(response => {
          console.log("Route data received:", response.data);
          let routeData = response.data;
          
          // Check if we have valid GeoJSON
          if (!routeData || !routeData.type || !routeData.features || routeData.features.length === 0) {
            console.log("No route data available from database");
            this.totalDistance = '0';
            this.totalDeliveries = '0';
            this.avgTime = '0';
            this.efficiency = '0';
            this.isLoading = false;
            return;
          }
            // Apply vehicle filter if needed
          if (this.vehicleFilter !== 'all') {
            // Filter data to show only the selected vehicle
            routeData = {
              type: 'FeatureCollection',
              features: routeData.features.filter(feature => 
                feature.properties.vehicle === this.vehicleFilter
              )
            };
            console.log(`Filtered to ${routeData.features.length} routes for vehicle ${this.vehicleFilter}`);
          }
          
          // Add the filtered route data as a new layer
          this.addRouteHistoryLayer(routeData);
          console.log("Added route history with:", routeData.features.length, "routes");
          
          // Apply date-based filtering on the server side
          return AnalyticsService.getRouteData({
            dateRange: this.dateRange,
            vehicleFilter: this.vehicleFilter
          });
        })
        .then(response => {
          // Handle response from date-based filtering
          const filteredData = response.data;
          
          // Clear existing route history layers
          this.clearRouteHistoryLayers();
          
          // Add the filtered route data as a new layer
          this.addRouteHistoryLayer(filteredData);
          console.log("Added filtered route history with:", filteredData.features.length, "routes");
          
          // Recalculate analytics
          this.calculateAnalytics();
        })
        .catch(error => {
          console.error('Error applying filters:', error);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
      /**
     * Load data for the catalog sections
     */
    loadCatalogData() {
      this.isLoadingCatalogData = true;
      
      // Load saved routes
      AnalyticsService.getSavedRoutes()
        .then(response => {
          // Process routes data
          this.catalogData.routes = response.data.map((route, index) => ({
            id: route.id || `route-${index}`,
            name: route.name || `Route ${index + 1}`,
            date: route.timestamp || route.date || 'Unknown date',
            data: route
          }));
        })
        .catch(error => {
          console.error('Error loading saved routes:', error);
          this.catalogData.routes = []; // No static fallback data
        });
      
      // Load locations
      AnalyticsService.getLocations()
        .then(response => {
          // Process locations data
          this.catalogData.locations = response.data.map(loc => ({
            id: loc.id,
            name: loc.name || `Location ${loc.id}`,
            type: loc.type || loc.location_type || 'unknown',
            coordinates: loc.coordinates,
            data: loc
          }));
        })
        .catch(error => {
          console.error('Error loading locations:', error);
          this.catalogData.locations = []; // No static fallback data
        });
        
      // Load order points
      AnalyticsService.getOrderPoints()
        .then(response => {
          // Process order points data from MongoDB
          this.catalogData.orders = response.data.map((order, index) => ({            id: order.order_id || `order-${index}`,
            name: `Order ${order.order_id || index}`,
            status: order.order_status || 'Unknown',
            coordinates: order.geometry ? order.geometry.coordinates : null,
            h3_index: order.h3_index,
            data: order
          }));
          console.log(`Loaded ${this.catalogData.orders.length} orders from MongoDB`);        })
        .catch(error => {
          console.error('Error loading order points:', error);
          this.catalogData.orders = []; // No static fallback data
        });
        
      // Load analytics locations (malls, retail stores)
      AnalyticsService.getAnalyticsLocations()
        .then(response => {
          // Process analytics locations data
          this.catalogData.analyticsLocations = response.data.data.map(location => ({
            id: location.id || location._id,
            name: location.name || 'Unnamed Location',
            category: location.category || 'Unknown',
            type: location.type || 'Unknown',
            coordinates: location.coordinates || [location.lon, location.lat],
            h3_index_res7: location.h3_index_res7,
            h3_index_res9: location.h3_index_res9,
            data: location
          }));
          
          // Set up filter categories
          this.analyticsFilter.availableCategories = response.data.categories || [];
          this.filteredAnalyticsLocations = this.catalogData.analyticsLocations;
          
          console.log(`Loaded ${this.catalogData.analyticsLocations.length} analytics locations from MongoDB`);
        })
        .catch(error => {
          console.error('Error loading analytics locations:', error);
          this.catalogData.analyticsLocations = [];
        });
        
      // Load analytics location statistics
      AnalyticsService.getAnalyticsLocationStats()
        .then(response => {
          this.analyticsStats = response.data;
        })
        .catch(error => {
          console.error('Error loading analytics location stats:', error);
          this.analyticsStats = null;
        })
        .finally(() => {
          this.isLoadingCatalogData = false;
        });
    },
      /**
     * Select a data item from the catalog
     */
    selectDataItem(item, type) {
      this.selectedData = {
        id: item.id,
        type: type,
        data: item.data
      };
      
      // Visualize the selected data on the map
      switch(type) {        case 'route':
          this.visualizeRoute(item);
          break;
        case 'location':
          this.visualizeLocation(item);
          break;
        case 'order':
          this.visualizeOrderPoint(item);
          break;
        case 'analytics':
          this.visualizeAnalyticsLocation(item);
          break;
      }
    },
      /**
     * Visualize a route on the map
     */
    visualizeRoute(route) {
      if (!this.map || !route.data) return;
      
      // Clear existing routes
      this.clearRouteHistoryLayers();
      
      // Add the selected route to the map
      this.addRouteHistoryLayer(route.data);
      
      // Calculate analytics for this route
      this.calculateAnalytics();
      
      // Update layer state in panel
      this.updateLayerStateAfterDataLoad('routes', true);
    },
    
    /**
     * Visualize a location on the map
     */
    visualizeLocation(location) {
      if (!this.map || !location.coordinates) return;
      
      // Center the map on the location
      this.map.flyTo({
        center: location.coordinates,
        zoom: 14
      });
      
      // Add a marker for the location
      const el = document.createElement('div');
      el.className = location.type === 'depot' ? 'depot-marker' : 'demand-marker';
      el.style.width = '20px';
      el.style.height = '20px';
      el.style.borderRadius = '50%';
      el.style.backgroundColor = location.type === 'depot' ? '#ff0000' : '#3388ff';
      el.style.border = '2px solid white';
      
      new mapboxgl.Marker(el)
        .setLngLat(location.coordinates)
        .setPopup(new mapboxgl.Popup({ offset: 25 }).setText(location.name))
        .addTo(this.map);
    },
    
    /**
     * Visualize an order point on the map
     */
    visualizeOrderPoint(order) {
      if (!this.map || !order.coordinates) return;
      
      // Center the map on the order point
      this.map.flyTo({
        center: order.coordinates,
        zoom: 15
      });
      
      // Add a marker for the order point
      const el = document.createElement('div');
      el.className = 'order-marker';
      el.style.width = '20px';
      el.style.height = '20px';
      el.style.borderRadius = '50%';
      el.style.backgroundColor = this.getStatusColor(order.status);
      el.style.border = '2px solid white';
      
      new mapboxgl.Marker(el)
        .setLngLat(order.coordinates)
        .setPopup(new mapboxgl.Popup({ offset: 25 }).setText(`${order.name} - ${order.status}`))
        .addTo(this.map);
    },
      /**
     * Get color based on order status
     */
    getStatusColor(status) {
      switch(status.toLowerCase()) {
        case 'delivered': return '#4caf50'; // Green
        case 'in transit': return '#ff9800'; // Orange
        default: return '#2196f3'; // Blue
      }
    },

    /**
     * Load orders configuration from backend
     */
    async loadOrdersConfiguration() {
      try {
        const response = await AnalyticsService.getOrderColumns();
        if (response.data && response.data.columns) {
          this.ordersAnalysis.availableColumns = response.data.columns;
        }
        if (response.data && response.data.aggregations) {
          this.ordersAnalysis.availableAggregations = response.data.aggregations;
        }
      } catch (error) {
        console.warn('Could not load orders configuration:', error);
        // Keep default configuration if backend is not available
      }
    },    /**
     * Apply orders analysis - triggered by button click
     */
    async applyOrdersAnalysis() {
      if (!this.ordersAnalysis.selectedColumn || !this.ordersAnalysis.selectedAggregation) {
        alert('Please select both column and aggregation method');
        return;
      }

      this.isLoadingOrdersAnalysis = true;
      
      try {
        // Clear existing visualizations
        this.clearOrdersVisualizations();

        if (this.ordersAnalysis.selectedViewType === 'hexagon') {
          // Use hexagon aggregation API
          const response = await AnalyticsService.aggregateOrders({
            column: this.ordersAnalysis.selectedColumn,
            aggregation: this.ordersAnalysis.selectedAggregation,
            h3_resolution: this.ordersAnalysis.selectedHexagonSize
          });          if (response.data && response.data.data) {
            this.ordersAnalysis.results = response.data;
            this.visualizeOrdersHexagons(response.data.data);
            // Mark orders analysis layer visible in panel
            this.updateLayerStateAfterDataLoad('ordersAnalysis', true);
          }
        } else {
          // Use raw order points for heatmap and point views
          const response = await AnalyticsService.getOrderPoints();
          
          if (response.data && response.data.length > 0) {
            this.ordersAnalysis.results = { 
              total_orders: response.data.length,
              data: response.data 
            };
              if (this.ordersAnalysis.selectedViewType === 'heatmap') {
              this.visualizeOrdersHeatmap(response.data);
            } else if (this.ordersAnalysis.selectedViewType === 'points') {
              this.visualizeOrdersPoints(response.data);
            }
            // Mark orders analysis layer visible in panel
            this.updateLayerStateAfterDataLoad('ordersAnalysis', true);
          }
        }
      } catch (error) {
        console.error('Error applying orders analysis:', error);
        alert('Failed to analyze orders data. Please check console for details.');
      } finally {
        this.isLoadingOrdersAnalysis = false;
      }
    },

    /**
     * Update orders visualization when dropdown values change
     */
    updateOrdersVisualization() {
      // Optional: Auto-apply when values change
      // this.applyOrdersAnalysis();
    },

    /**
     * Get column label by name
     */
    getColumnLabel(columnName) {
      const column = this.ordersAnalysis.availableColumns.find(col => col.name === columnName);
      return column ? column.label : columnName;
    },

    /**
     * Visualize orders data as H3 hexagons on the map
     */    visualizeOrdersHexagons(hexagonData) {
      if (!this.map || !hexagonData || hexagonData.length === 0) return;      // Remove existing hexagon layers
      this.clearOrdersHexagons();

      // Create GeoJSON features for hexagons
      const hexagonFeatures = hexagonData.map(hex => {
        try {
          // Get hexagon boundary coordinates using h3-js
          const hexBoundary = cellToBoundary(hex.h3_index);
          
          // Convert to GeoJSON polygon format (note: h3 returns [lat, lng], we need [lng, lat])
          const coordinates = [hexBoundary.map(coord => [coord[1], coord[0]])];
          
          return {
            type: 'Feature',
            properties: {
              h3_index: hex.h3_index,
              value: hex.value,
              count: hex.count
            },
            geometry: {
              type: 'Polygon',
              coordinates: coordinates
            }
          };
        } catch (error) {
          console.warn(`Error creating hexagon for h3_index ${hex.h3_index}:`, error);
          return null;
        }
      }).filter(feature => feature !== null);

      if (hexagonFeatures.length === 0) {
        alert('No valid hexagons could be created from the data');
        return;
      }

      const hexagonGeoJSON = {
        type: 'FeatureCollection',
        features: hexagonFeatures
      };

      // Add source for hexagons
      this.map.addSource('orders-hexagons', {
        type: 'geojson',
        data: hexagonGeoJSON
      });

      // Calculate color scale based on values
      const values = hexagonData.map(hex => hex.value);
      const maxValue = Math.max(...values);
      const minValue = Math.min(...values);      // Add fill layer for hexagons
      this.map.addLayer({
        id: 'orders-hexagons-fill',
        type: 'fill',
        source: 'orders-hexagons',
        layout: {
          'visibility': this.layers.ordersAnalysis.visible ? 'visible' : 'none'
        },
        paint: {          'fill-color': this.getColorStops(values, minValue, maxValue),
          'fill-opacity': 0.8
        }
      });      // Add stroke layer for hexagons
      this.map.addLayer({
        id: 'orders-hexagons-stroke',
        type: 'line',
        source: 'orders-hexagons',
        layout: {
          'visibility': this.layers.ordersAnalysis.visible ? 'visible' : 'none'
        },
        paint: {
          'line-color': '#0f172a',
          'line-width': 0.5,
          'line-opacity': 0.6
        }
      });

      // Add click event for hexagons
      this.map.on('click', 'orders-hexagons-fill', (e) => {
        const properties = e.features[0].properties;
        const aggregationLabel = this.ordersAnalysis.availableAggregations.find(
          agg => agg.name === this.ordersAnalysis.selectedAggregation
        )?.label || this.ordersAnalysis.selectedAggregation;
        
        const columnLabel = this.getColumnLabel(this.ordersAnalysis.selectedColumn);
        
        new mapboxgl.Popup()
          .setLngLat(e.lngLat)
          .setHTML(`
            <div>
              <h4>Hexagon Analysis</h4>
              <p><strong>H3 Index:</strong> ${properties.h3_index}</p>
              <p><strong>${aggregationLabel} of ${columnLabel}:</strong> ${properties.value.toFixed(2)}</p>
              <p><strong>Orders Count:</strong> ${properties.count}</p>
            </div>
          `)
          .addTo(this.map);
      });

      // Change cursor on hover
      this.map.on('mouseenter', 'orders-hexagons-fill', () => {
        this.map.getCanvas().style.cursor = 'pointer';
      });

      this.map.on('mouseleave', 'orders-hexagons-fill', () => {
        this.map.getCanvas().style.cursor = '';
      });

      console.log(`Added ${hexagonFeatures.length} hexagons to the map`);
    },    /**
     * Clear orders hexagon layers
     */
    clearOrdersHexagons() {
      ['orders-hexagons-fill', 'orders-hexagons-stroke'].forEach(layerId => {
        if (this.map.getLayer(layerId)) {
          this.map.removeLayer(layerId);
        }
      });

      if (this.map.getSource('orders-hexagons')) {
        this.map.removeSource('orders-hexagons');
      }
    },    /**
     * Clear all orders visualizations
     */
    clearOrdersVisualizations() {
      // Clear hexagons
      this.clearOrdersHexagons();
      
      // Clear heatmap
      if (this.map.getLayer('orders-heatmap-layer')) {
        this.map.removeLayer('orders-heatmap-layer');
      }
      if (this.map.getSource('orders-heatmap')) {
        this.map.removeSource('orders-heatmap');
      }
      
      // Clear points
      if (this.map.getLayer('orders-points-layer')) {
        this.map.removeLayer('orders-points-layer');
      }
      if (this.map.getSource('orders-points')) {
        this.map.removeSource('orders-points');
      }
      
      // Reset layer state in panel
      this.resetLayerState('ordersAnalysis');
    },

    /**
     * Visualize orders data as heatmap
     */
    visualizeOrdersHeatmap(ordersData) {
      if (!this.map || !ordersData || ordersData.length === 0) return;

      // Create GeoJSON features for heatmap
      const heatmapFeatures = ordersData
        .filter(order => order.geometry && order.geometry.coordinates)
        .map(order => {
          // Get the value for the selected column
          const value = this.getOrderValue(order, this.ordersAnalysis.selectedColumn);
          
          return {
            type: 'Feature',
            properties: {
              weight: value || 1,
              orderId: order.Siparis_ID,
              value: value
            },
            geometry: order.geometry
          };
        });

      const heatmapGeoJSON = {
        type: 'FeatureCollection',
        features: heatmapFeatures
      };

      // Add source for heatmap
      this.map.addSource('orders-heatmap', {
        type: 'geojson',
        data: heatmapGeoJSON
      });      // Add heatmap layer
      this.map.addLayer({
        id: 'orders-heatmap-layer',
        type: 'heatmap',
        source: 'orders-heatmap',
        layout: {
          'visibility': this.layers.ordersAnalysis.visible ? 'visible' : 'none'
        },
        paint: {
          'heatmap-weight': ['get', 'weight'],
          'heatmap-intensity': this.ordersAnalysis.heatmapSettings.intensity,
          'heatmap-color': [
            'interpolate',
            ['linear'],
            ['heatmap-density'],
            0, 'rgba(33,102,172,0)',
            0.2, 'rgb(103,169,207)',
            0.4, 'rgb(209,229,240)',
            0.6, 'rgb(253,219,199)',
            0.8, 'rgb(239,138,98)',
            1, 'rgb(178,24,43)'
          ],
          'heatmap-radius': this.ordersAnalysis.heatmapSettings.radius,
          'heatmap-opacity': 0.8
        }
      });

      console.log(`Added heatmap with ${heatmapFeatures.length} order points`);
    },

    /**
     * Visualize orders data as individual points
     */
    visualizeOrdersPoints(ordersData) {
      if (!this.map || !ordersData || ordersData.length === 0) return;

      // Create GeoJSON features for points
      const pointFeatures = ordersData
        .filter(order => order.geometry && order.geometry.coordinates)
        .map(order => {
          const value = this.getOrderValue(order, this.ordersAnalysis.selectedColumn);
          
          return {
            type: 'Feature',
            properties: {              orderId: order.order_id,
              value: value,
              status: order.order_status,
              deliveryCost: order.delivery_cost,
              distance: order.delivery_lmd_cost,
              weight: order.cargo_weight,
              volume: order.cargo_volume,
              customerName: order.customer_name,
              address: order.delivery_address || 'N/A'
            },
            geometry: order.geometry
          };
        });

      const pointsGeoJSON = {
        type: 'FeatureCollection',
        features: pointFeatures
      };

      // Calculate value scale for point sizing and coloring
      const values = pointFeatures.map(f => f.properties.value).filter(v => v != null);
      const maxValue = Math.max(...values);
      const minValue = Math.min(...values);

      // Add source for points
      this.map.addSource('orders-points', {
        type: 'geojson',
        data: pointsGeoJSON
      });      // Add points layer
      this.map.addLayer({
        id: 'orders-points-layer',
        type: 'circle',
        source: 'orders-points',
        layout: {
          'visibility': this.layers.ordersAnalysis.visible ? 'visible' : 'none'
        },
        paint: {
          'circle-radius': [
            'interpolate',
            ['linear'],
            ['get', 'value'],
            minValue, 4,
            maxValue, 12
          ],
          'circle-color': this.getColorStops(values, minValue, maxValue),
          'circle-stroke-width': 1,
          'circle-stroke-color': '#ffffff',
          'circle-opacity': 0.8
        }
      });

      // Add click event for points
      this.map.on('click', 'orders-points-layer', (e) => {
        const properties = e.features[0].properties;
        const columnLabel = this.getColumnLabel(this.ordersAnalysis.selectedColumn);
        
        new mapboxgl.Popup()
          .setLngLat(e.lngLat)
          .setHTML(`
            <div class="order-popup">
              <h4>Order Details</h4>
              <p><strong>Order ID:</strong> ${properties.orderId}</p>
              <p><strong>Customer:</strong> ${properties.customerName || 'N/A'}</p>
              <p><strong>Status:</strong> ${properties.status || 'N/A'}</p>
              <p><strong>${columnLabel}:</strong> ${properties.value ? properties.value.toFixed(2) : 'N/A'}</p>
              <p><strong>Delivery Cost:</strong> ${properties.deliveryCost ? properties.deliveryCost.toFixed(2) + ' TL' : 'N/A'}</p>
              <p><strong>Distance:</strong> ${properties.distance ? properties.distance.toFixed(2) + ' km' : 'N/A'}</p>
              <p><strong>Weight:</strong> ${properties.weight ? properties.weight.toFixed(2) + ' kg' : 'N/A'}</p>
              <p><strong>Address:</strong> ${properties.address || 'N/A'}</p>
            </div>
          `)
          .addTo(this.map);
      });

      // Change cursor on hover
      this.map.on('mouseenter', 'orders-points-layer', () => {
        this.map.getCanvas().style.cursor = 'pointer';
      });

      this.map.on('mouseleave', 'orders-points-layer', () => {
        this.map.getCanvas().style.cursor = '';
      });

      console.log(`Added ${pointFeatures.length} order points to the map`);
    },

    /**
     * Get value from order data based on selected column
     */
    getOrderValue(order, columnName) {
      return order[columnName] || 0;
    },

    /**
     * Get view type label by name
     */
    getViewTypeLabel(viewTypeName) {
      const viewType = this.ordersAnalysis.availableViewTypes.find(vt => vt.name === viewTypeName);
      return viewType ? viewType.label : viewTypeName;
    },

    /**
     * Get color stops based on selected color palette and distribution method
     */
    getColorStops(values, minValue, maxValue) {
      const selectedPalette = this.ordersAnalysis.availableColorPalettes.find(
        p => p.name === this.ordersAnalysis.selectedColorPalette
      );
      const colors = selectedPalette ? selectedPalette.colors : this.ordersAnalysis.availableColorPalettes[0].colors;
      
      // Calculate breakpoints based on distribution method
      const breakpoints = this.calculateBreakpoints(values, minValue, maxValue);
      
      // Create color stops array for Mapbox
      const colorStops = ['interpolate', ['linear'], ['get', 'value']];
      
      for (let i = 0; i < breakpoints.length && i < colors.length; i++) {
        colorStops.push(breakpoints[i], colors[i]);
      }
      
      return colorStops;
    },

    /**
     * Calculate breakpoints based on selected distribution method
     */
    calculateBreakpoints(values, minValue, maxValue) {
      const numGroups = this.ordersAnalysis.numberOfGroups;
      const distribution = this.ordersAnalysis.selectedDistribution;
      
      switch (distribution) {
        case 'linear':
          return this.calculateLinearBreakpoints(minValue, maxValue, numGroups);
        case 'quantile':
          return this.calculateQuantileBreakpoints(values, numGroups);
        case 'logarithmic':
          return this.calculateLogarithmicBreakpoints(minValue, maxValue, numGroups);
        case 'exponential':
          return this.calculateExponentialBreakpoints(minValue, maxValue, numGroups);
        default:
          return this.calculateLinearBreakpoints(minValue, maxValue, numGroups);
      }
    },

    /**
     * Calculate linear distribution breakpoints
     */
    calculateLinearBreakpoints(minValue, maxValue, numGroups) {
      const breakpoints = [];
      const step = (maxValue - minValue) / (numGroups - 1);
      
      for (let i = 0; i < numGroups; i++) {
        breakpoints.push(minValue + (step * i));
      }
      
      return breakpoints;
    },

    /**
     * Calculate quantile distribution breakpoints
     */
    calculateQuantileBreakpoints(values, numGroups) {
      const sortedValues = [...values].sort((a, b) => a - b);
      const breakpoints = [];
      
      for (let i = 0; i < numGroups; i++) {
        const quantile = i / (numGroups - 1);
        const index = Math.floor(quantile * (sortedValues.length - 1));
        breakpoints.push(sortedValues[index] || sortedValues[0]);
      }
      
      return breakpoints;
    },

    /**
     * Calculate logarithmic distribution breakpoints
     */
    calculateLogarithmicBreakpoints(minValue, maxValue, numGroups) {
      const breakpoints = [];
      
      // Ensure positive values for logarithm
      const logMin = Math.log(Math.max(minValue, 0.01));
      const logMax = Math.log(Math.max(maxValue, 0.01));
      const logStep = (logMax - logMin) / (numGroups - 1);
      
      for (let i = 0; i < numGroups; i++) {
        breakpoints.push(Math.exp(logMin + (logStep * i)));
      }
      
      return breakpoints;
    },

    /**
     * Calculate exponential distribution breakpoints
     */
    calculateExponentialBreakpoints(minValue, maxValue, numGroups) {
      const breakpoints = [];
      const base = Math.pow(maxValue / Math.max(minValue, 0.01), 1 / (numGroups - 1));
      
      for (let i = 0; i < numGroups; i++) {
        breakpoints.push(minValue * Math.pow(base, i));
      }
      
      return breakpoints;
    },
    
    // Maintain existing methods
    goBack() {
      // Emit an event to tell the parent component to switch back to the main view
      this.$emit('close-analytics');
    },

    /**
     * Toggle layer visibility on the map
     */
    toggleLayerVisibility(layerKey) {
      const entry = this.layers[layerKey];
      if (!entry || !entry.ids) return;
      
      // If layer is being turned on and no data exists, load the data first
      if (!entry.visible) {
        this.loadLayerData(layerKey);
      } else {
        // If layer is being turned off, just hide it
        this.updateLayerVisibility(layerKey, false);
      }
    },

    /**
     * Load data for a specific layer when toggled to visible
     */    async loadLayerData(layerKey) {
      try {
        switch(layerKey) {
          // ...existing cases...
          case 'ordersAnalysis':
            if (this.catalogData.orders.length > 0) {
              await this.applyOrdersAnalysis();
            } else {
              console.warn('No orders data available for analysis');
              this.layers[layerKey].visible = false;
            }
            break;
          case 'analyticsLocations':
            if (this.catalogData.analyticsLocations.length > 0) {
              await this.loadAnalyticsLocationsToMap();
            } else {
              console.warn('No analytics locations data available');
              this.layers[layerKey].visible = false;
            }
            break;
          // ...existing cases...
        }
      } catch (error) {
        console.error(`Error loading data for layer ${layerKey}:`, error);
        this.layers[layerKey].visible = false;
      }
    },

    /**
     * Update layer visibility without reloading data
     */
    updateLayerVisibility(layerKey, visible = null) {
      const entry = this.layers[layerKey];
      if (!entry || !entry.ids) return;
      
      // Use provided visibility or toggle current state
      const newVisibility = visible !== null ? visible : !entry.visible;
      entry.visible = newVisibility;
      
      const layoutVisibility = newVisibility ? 'visible' : 'none';
      
      // Apply to each Mapbox layer id
      entry.ids.forEach(id => {
        if (this.map.getLayer(id)) {
          this.map.setLayoutProperty(id, 'visibility', layoutVisibility);
        }
      });
    },    /**
     * Update layer state when data is loaded from left panel
     */
    updateLayerStateAfterDataLoad(layerKey, visible = true) {
      if (this.layers[layerKey]) {
        this.layers[layerKey].visible = visible;
        // Also update the actual Mapbox layer visibility
        this.updateLayerVisibility(layerKey, visible);
      }
    },

    /**
     * Clear and reset layer state
     */
    resetLayerState(layerKey) {
      if (this.layers[layerKey]) {        this.layers[layerKey].visible = false;
      }
    },

    // Analytics locations methods
    filterAnalyticsLocations() {
      if (this.analyticsFilter.selectedCategory === '') {
        this.filteredAnalyticsLocations = this.catalogData.analyticsLocations;
      } else {
        this.filteredAnalyticsLocations = this.catalogData.analyticsLocations.filter(
          location => location.category === this.analyticsFilter.selectedCategory
        );
      }
    },

    updateAnalyticsVisualization() {
      // Update visualization settings
      console.log('Updating analytics visualization settings:', this.analyticsVisualization);
    },

    async loadAnalyticsLocationsToMap() {
      this.isLoadingAnalyticsVisualization = true;
      
      try {
        // Clear existing analytics layers
        this.clearAnalyticsLayers();
        
        // Get filtered data for map
        const params = {};
        if (this.analyticsFilter.selectedCategory) {
          params.category = this.analyticsFilter.selectedCategory;
        }
        
        // Load GeoJSON data for visualization
        const response = await AnalyticsService.getAnalyticsLocationsGeoJSON(params);
        const geojsonData = response.data;
        
        if (this.analyticsVisualization.showPoints) {
          this.addAnalyticsPointsLayer(geojsonData);
        }
        
        if (this.analyticsVisualization.showHexagons) {
          await this.addAnalyticsHexagonsLayer();
        }
        
        // Update layer visibility
        this.layers.analyticsLocations.visible = true;
        
        // Fit map to show analytics locations
        if (geojsonData.features.length > 0) {
          const bounds = new mapboxgl.LngLatBounds();
          geojsonData.features.forEach(feature => {
            bounds.extend(feature.geometry.coordinates);
          });
          this.map.fitBounds(bounds, { padding: 50 });
        }
        
      } catch (error) {
        console.error('Error loading analytics locations to map:', error);
      } finally {
        this.isLoadingAnalyticsVisualization = false;
      }
    },

    addAnalyticsPointsLayer(geojsonData) {
      // Add source
      this.map.addSource('analytics-locations-source', {
        type: 'geojson',
        data: geojsonData
      });

      // Add points layer
      this.map.addLayer({
        id: 'analytics-locations-points',
        type: 'circle',
        source: 'analytics-locations-source',
        paint: {
          'circle-radius': [
            'case',
            ['==', ['get', 'category'], 'shopping_mall'], 8,
            ['==', ['get', 'category'], 'shopping_center'], 7,
            ['==', ['get', 'category'], 'department_store'], 6,
            5
          ],
          'circle-color': [
            'case',
            ['==', ['get', 'category'], 'shopping_mall'], '#e74c3c',
            ['==', ['get', 'category'], 'shopping_center'], '#f39c12',
            ['==', ['get', 'category'], 'department_store'], '#3498db',
            ['==', ['get', 'category'], 'supermarket'], '#27ae60',
            '#95a5a6'
          ],
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff'
        }
      });      // Add click handler
      this.map.on('click', 'analytics-locations-points', (e) => {
        const coordinates = e.features[0].geometry.coordinates.slice();
        const properties = e.features[0].properties;
        
        // Create popup with location details and "Add to Locations" button
        const popupContent = this.createLocationPopupContent(properties, coordinates);
        
        new mapboxgl.Popup()
          .setLngLat(coordinates)
          .setHTML(popupContent)
          .addTo(this.map);
      });

      // Change cursor on hover
      this.map.on('mouseenter', 'analytics-locations-points', () => {
        this.map.getCanvas().style.cursor = 'pointer';
      });

      this.map.on('mouseleave', 'analytics-locations-points', () => {
        this.map.getCanvas().style.cursor = '';
      });
    },

    async addAnalyticsHexagonsLayer() {
      try {
        // Group locations by H3 hexagon
        const hexagonCounts = {};
        this.filteredAnalyticsLocations.forEach(location => {
          const h3Index = location[`h3_index_res${this.analyticsVisualization.hexagonResolution}`];
          if (h3Index) {
            hexagonCounts[h3Index] = (hexagonCounts[h3Index] || 0) + 1;
          }
        });

        // Create hexagon features
        const { cellToBoundary } = await import('h3-js');
        const hexagonFeatures = Object.entries(hexagonCounts).map(([h3Index, count]) => {
          const boundary = cellToBoundary(h3Index, true); // true for [lat, lng] format
          const coordinates = [boundary.map(coord => [coord[1], coord[0]])]; // Convert to [lng, lat]

          return {
            type: 'Feature',
            properties: {
              h3_index: h3Index,
              count: count,
              density: count / this.filteredAnalyticsLocations.length
            },
            geometry: {
              type: 'Polygon',
              coordinates: coordinates
            }
          };
        });

        const hexagonGeoJSON = {
          type: 'FeatureCollection',
          features: hexagonFeatures
        };

        // Add hexagon source
        this.map.addSource('analytics-hexagons-source', {
          type: 'geojson',
          data: hexagonGeoJSON
        });

        // Add fill layer
        this.map.addLayer({
          id: 'analytics-locations-hexagons-fill',
          type: 'fill',
          source: 'analytics-hexagons-source',
          paint: {
            'fill-color': [
              'interpolate',
              ['linear'],
              ['get', 'count'],
              1, '#f7f7f7',
              5, '#41b6c4',
              10, '#2c7fb8',
              20, '#253494'
            ],
            'fill-opacity': 0.6
          }
        });

        // Add stroke layer
        this.map.addLayer({
          id: 'analytics-locations-hexagons-stroke',
          type: 'line',
          source: 'analytics-hexagons-source',
          paint: {
            'line-color': '#2c3e50',
            'line-width': 1,
            'line-opacity': 0.8
          }
        });        // Add click handler for hexagons
        this.map.on('click', 'analytics-locations-hexagons-fill', (e) => {
          const properties = e.features[0].properties;
          const coordinates = e.lngLat;
          
          // Create hexagon popup content
          const hexagonPopupContent = this.createHexagonPopupContent(properties);
          
          new mapboxgl.Popup()
            .setLngLat(coordinates)
            .setHTML(hexagonPopupContent)
            .addTo(this.map);
        });

      } catch (error) {
        console.error('Error adding analytics hexagons layer:', error);
      }
    },

    visualizeAnalyticsLocation(item) {
      if (!item.coordinates) return;
      
      // Center map on the location
      this.map.flyTo({
        center: item.coordinates,
        zoom: 15,
        duration: 1000
      });

      // Add a temporary marker
      const marker = new mapboxgl.Marker({ color: '#e74c3c' })
        .setLngLat(item.coordinates)
        .setPopup(new mapboxgl.Popup().setHTML(`
          <div>
            <h4>${item.name}</h4>
            <p><strong>Category:</strong> ${item.category}</p>
            <p><strong>Type:</strong> ${item.type}</p>
          </div>
        `))
        .addTo(this.map);

      // Remove marker after 5 seconds
      setTimeout(() => {
        marker.remove();
      }, 5000);
    },

    clearAnalyticsLayers() {
      const layerIds = this.layers.analyticsLocations.ids;
      
      layerIds.forEach(layerId => {
        if (this.map.getLayer(layerId)) {
          this.map.removeLayer(layerId);
        }
      });

      const sourceIds = ['analytics-locations-source', 'analytics-hexagons-source'];
      sourceIds.forEach(sourceId => {
        if (this.map.getSource(sourceId)) {
          this.map.removeSource(sourceId);
        }      });
    },

    /**
     * Create popup content for analytics location with details and "Add to Locations" button
     */
    createLocationPopupContent(properties, coordinates) {
      const buttonId = `add-location-btn-${Date.now()}`;
      
      const popupContent = `
        <div class="analytics-location-popup">
          <h4>${properties.name || 'Unnamed Location'}</h4>
          <div class="location-details">
            <p><strong>Category:</strong> ${properties.category || 'N/A'}</p>
            <p><strong>Type:</strong> ${properties.type || 'N/A'}</p>
            <p><strong>Address:</strong> ${properties.address || 'N/A'}</p>
            <p><strong>Coordinates:</strong> ${coordinates[1].toFixed(6)}, ${coordinates[0].toFixed(6)}</p>
            ${properties.osm_id ? `<p><strong>OSM ID:</strong> ${properties.osm_id}</p>` : ''}
          </div>
          <div class="popup-actions">
            <button id="${buttonId}" class="add-location-btn">
              <i class="fas fa-plus"></i> Add to Locations
            </button>
          </div>
        </div>
      `;
      
      // Add event listener after popup is created
      setTimeout(() => {
        const button = document.getElementById(buttonId);
        if (button) {
          button.addEventListener('click', () => {
            this.addAnalyticsLocationToMoventa(properties, coordinates);
          });
        }
      }, 100);
      
      return popupContent;
    },

    /**
     * Add an analytics location to the main moventa.locations collection
     */
    async addAnalyticsLocationToMoventa(properties, coordinates) {
      try {
        // Show loading state
        const button = document.querySelector('.add-location-btn');
        if (button) {
          button.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Adding...';
          button.disabled = true;
        }

        // Create location data object following the expected format
        const locationData = {
          name: properties.name || 'Retail Location',
          type: 'demand', // Retail locations are demand points
          coordinates: coordinates,
          address: properties.address || '',
          category: properties.category || 'retail',
          // Demand point specific fields
          demand: 1, // Default demand
          timeWindowStart: '09:00',
          timeWindowEnd: '17:00',
          vehicleCapability: 'any',
          assignedDepot: null,
          // Additional metadata
          source: 'analytics_locations',
          originalOsmId: properties.osm_id || null,
          originalCategory: properties.category || null,
          dateAdded: new Date().toISOString()
        };        // Call the LocationsService to create the location
        const response = await LocationsService.createLocation(locationData);
        
        console.log('Location added successfully:', response.data);
        
        // Show success message
        if (button) {
          button.innerHTML = '<i class="fas fa-check"></i> Added!';
          button.style.backgroundColor = '#28a745';
        }
        
        // Show notification
        this.showNotification('Location added successfully to your locations list!', 'success');
        
        // Close the popup after a short delay
        setTimeout(() => {
          const popups = document.querySelectorAll('.mapboxgl-popup');
          popups.forEach(popup => popup.remove());
        }, 2000);

      } catch (error) {
        console.error('Error adding location:', error);
        
        // Show error state
        const button = document.querySelector('.add-location-btn');
        if (button) {
          button.innerHTML = '<i class="fas fa-exclamation-triangle"></i> Error';
          button.style.backgroundColor = '#dc3545';
          button.disabled = false;
        }
        
        // Show error notification
        this.showNotification('Failed to add location. Please try again.', 'error');
      }
    },

    /**
     * Show a notification message to the user
     */
    showNotification(message, type = 'info') {
      // Create notification element
      const notification = document.createElement('div');
      notification.className = `analytics-notification ${type}`;
      notification.innerHTML = `
        <div class="notification-content">
          <i class="fas fa-${type === 'success' ? 'check-circle' : type === 'error' ? 'exclamation-circle' : 'info-circle'}"></i>
          <span>${message}</span>
        </div>
      `;
      
      // Add to page
      document.body.appendChild(notification);
      
      // Show notification with animation
      setTimeout(() => {
        notification.classList.add('show');
      }, 100);
      
      // Remove notification after 5 seconds
      setTimeout(() => {
        notification.classList.remove('show');
        setTimeout(() => {
          if (notification.parentNode) {
            notification.parentNode.removeChild(notification);
          }
        }, 300);
      }, 5000);
    },

    /**
     * Create popup content for hexagon with statistics and option to view locations
     */
    createHexagonPopupContent(properties) {
      const buttonId = `view-locations-btn-${Date.now()}`;
      
      const popupContent = `
        <div class="analytics-hexagon-popup">
          <h4>Hexagon Statistics</h4>
          <div class="hexagon-details">
            <p><strong>Locations:</strong> ${properties.count}</p>
            <p><strong>Density:</strong> ${(properties.density * 100).toFixed(1)}%</p>
            <p><strong>Resolution:</strong> H3 Level ${this.analyticsVisualization.hexagonResolution}</p>
            <p><strong>H3 Index:</strong> ${properties.h3_index}</p>
          </div>
          <div class="popup-actions">
            <button id="${buttonId}" class="view-locations-btn">
              <i class="fas fa-eye"></i> View ${properties.count} Locations
            </button>
          </div>
        </div>
      `;
      
      // Add event listener after popup is created
      setTimeout(() => {
        const button = document.getElementById(buttonId);
        if (button) {
          button.addEventListener('click', () => {
            this.showHexagonLocations(properties.h3_index);
          });
        }
      }, 100);
      
      return popupContent;
    },

    /**
     * Show individual locations within a hexagon
     */
    async showHexagonLocations(h3Index) {
      try {
        // Find locations within this hexagon
        const locationsInHexagon = this.filteredAnalyticsLocations.filter(location => {
          const locationH3Index = location[`h3_index_res${this.analyticsVisualization.hexagonResolution}`];
          return locationH3Index === h3Index;
        });

        if (locationsInHexagon.length === 0) {
          this.showNotification('No locations found in this hexagon', 'info');
          return;
        }

        // Create bounds for all locations in the hexagon
        const bounds = new mapboxgl.LngLatBounds();
        locationsInHexagon.forEach(location => {
          if (location.coordinates) {
            bounds.extend(location.coordinates);
          }
        });

        // Fit map to bounds
        this.map.fitBounds(bounds, { padding: 100 });        // Add temporary markers for each location
        const tempMarkers = [];
        locationsInHexagon.forEach((location) => {
          if (location.coordinates) {
            const marker = new mapboxgl.Marker({ 
              color: '#e74c3c',
              scale: 0.8
            })
            .setLngLat(location.coordinates)
            .setPopup(new mapboxgl.Popup().setHTML(
              this.createLocationPopupContent(location, location.coordinates)
            ))
            .addTo(this.map);
            
            tempMarkers.push(marker);
          }
        });

        // Show notification
        this.showNotification(`Showing ${locationsInHexagon.length} locations in hexagon`, 'success');

        // Remove temporary markers after 30 seconds
        setTimeout(() => {
          tempMarkers.forEach(marker => marker.remove());
        }, 30000);

        // Close the hexagon popup
        const popups = document.querySelectorAll('.mapboxgl-popup');
        popups.forEach(popup => {
          if (popup.querySelector('.analytics-hexagon-popup')) {
            popup.remove();
          }
        });

      } catch (error) {
        console.error('Error showing hexagon locations:', error);
        this.showNotification('Error showing locations in hexagon', 'error');
      }
    },

    // ...existing code...
  }
}
</script>

<style scoped>
.analytics-map-container {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

/* New styles for the left panel */
.analytics-left-panel {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: 280px;
  background: white;
  box-shadow: 2px 0 10px rgba(0,0,0,0.1);
  z-index: 5;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.data-catalog-section,
.data-view-config {
  padding: 15px;
  border-bottom: 1px solid #eee;
}

.data-catalog-section h3,
.data-view-config h3 {
  margin-top: 0;
  margin-bottom: 15px;
  color: #2c3e50;
  font-size: 1.1rem;
  border-bottom: 1px solid #eee;
  padding-bottom: 8px;
}

/* Tab navigation */
.tab-nav {
  display: flex;
  margin-bottom: 10px;
}

.tab-nav button {
  flex: 1;
  padding: 8px 0;
  background: none;
  border: none;
  border-bottom: 2px solid #ddd;
  cursor: pointer;
  font-weight: bold;
  color: #666;
  transition: all 0.2s;
}

.tab-nav button.active {
  color: #3498db;
  border-bottom-color: #3498db;
}

/* Data lists */
.data-list {
  list-style: none;
  padding: 0;
  margin: 0;
  max-height: 300px;
  overflow-y: auto;
}

.data-list li {
  padding: 10px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: background-color 0.2s;
}

.data-list li:hover {
  background-color: #f5f7fa;
}

.data-list li.selected {
  background-color: #e3f2fd;
}

.item-name {
  display: block;
  font-weight: bold;
  color: #2c3e50;
}

.item-date,
.item-type,
.item-status {
  display: block;
  font-size: 0.8rem;
  color: #666;
  margin-top: 3px;
}

.empty-message,
.loading-message {
  padding: 15px;
  text-align: center;
  color: #666;
  font-style: italic;
}

/* View configuration */
.view-options {
  margin-bottom: 15px;
}

.view-option {
  margin-bottom: 10px;
}

.view-option label {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
}

.view-option input[type="checkbox"] {
  margin-right: 8px;
}

.heatmap-config {
  background-color: #f9f9f9;
  padding: 10px;
  border-radius: 4px;
  margin-top: 10px;
}

.config-item {
  margin-bottom: 10px;
}

.config-item label {
  display: block;
  margin-bottom: 5px;
  font-size: 0.85rem;
  color: #555;
}

.config-item input[type="range"] {
  width: 100%;
  margin-bottom: 5px;
}

/* Orders analysis styles */
.orders-analysis-controls {
  margin-bottom: 15px;
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 4px;
}

.orders-analysis-controls h4 {
  margin: 0 0 15px 0;
  color: #2c3e50;
  font-size: 1rem;
}

.analysis-control {
  margin-bottom: 12px;
}

.analysis-control label {
  display: block;
  margin-bottom: 5px;
  font-size: 0.85rem;
  font-weight: bold;
  color: #555;
}

.analysis-control select {
  width: 100%;
  padding: 6px 8px;
  border: 1px solid #ddd;
  border-radius: 3px;
  font-size: 0.85rem;
}

.apply-analysis-btn {
  width: 100%;
  padding: 8px 12px;
  background-color: #3498db;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  margin-bottom: 10px;
}

.apply-analysis-btn:hover:not(:disabled) {
  background-color: #2980b9;
}

.apply-analysis-btn:disabled {
  background-color: #bdc3c7;
  cursor: not-allowed;
}

.analysis-summary {
  background-color: white;
  padding: 8px;
  border-radius: 3px;
  margin-top: 10px;
}

.analysis-summary p {
  margin: 3px 0;
  font-size: 0.8rem;
  color: #666;
}

.orders-summary {
  background-color: white;
  padding: 10px;
  border-radius: 4px;
  text-align: center;
}

.orders-summary p {
  margin: 5px 0;
  font-size: 0.85rem;
  color: #666;
}

/* Range slider styling */
.range-slider {
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: #ddd;
  outline: none;
  -webkit-appearance: none;
  appearance: none;
  margin: 8px 0;
}

.range-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #3498db;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.range-slider::-moz-range-thumb {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #3498db;
  cursor: pointer;
  border: none;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.range-labels {
  display: flex;
  justify-content: space-between;
  font-size: 0.75rem;
  color: #666;
  margin-top: -5px;
}

/* Adjust position of existing controls to account for left panel */
.analytics-controls {
  left: 290px; /* 280px panel width + 10px gap */
}

/* New styles for the right panel */
.analytics-right-panel {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 220px;
  background: white;
  box-shadow: -2px 0 10px rgba(0,0,0,0.1);
  z-index: 5;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  padding: 15px;
}

.analytics-right-panel h3 {
  margin-top: 0;
  margin-bottom: 15px;
  color: #2c3e50;
  font-size: 1.1rem;
  border-bottom: 1px solid #eee;
  padding-bottom: 8px;
}

.layers-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.layer-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 10px;
  background-color: #f8f9fa;
  border-radius: 4px;
  transition: background-color 0.2s;
  user-select: none;
}

.layer-item:hover {
  background-color: #e9ecef;
}

.layer-name {
  font-size: 0.9rem;
  color: #2c3e50;
}

.layer-visibility-toggle {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.eye-icon {
  position: relative;
  display: inline-block;
  width: 18px;
  height: 18px;
}

.eye-icon:before {
  content: '';
  position: absolute;
  top: 9px;
  left: 1px;
  width: 16px;
  height: 1px;
  background-color: #777;
  transform: rotate(45deg);
  opacity: 0;
  transition: opacity 0.2s;
}

.eye-icon:after {
  content: '';
  position: absolute;
  width: 16px;
  height: 8px;
  border: 1px solid #777;
  border-radius: 8px 8px 0 0;
  border-bottom: none;
  top: 5px;
  left: 1px;
}

.layer-visibility-toggle.visible .eye-icon:after {
  border-color: #3498db;
}

.layer-visibility-toggle:not(.visible) .eye-icon:before {
  opacity: 1;
}

/* Adjust position of existing controls to account for right panel */
.analytics-controls {
  right: 230px; /* 220px panel width + 10px gap */
}

.back-button {
  position: absolute;
  bottom: 20px;
  left: 290px; /* Adjust to account for left panel */
  background: white;
  padding: 10px 15px;
  border-radius: 4px;
  box-shadow: 0 0 10px rgba(0,0,0,0.1);
  cursor: pointer;
  font-weight: bold;
  z-index: 1;
}

/* Existing loading overlay styles remain unchanged */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.7);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-text {
  margin-top: 15px;
  font-size: 1rem;
  color: #2c3e50;
}

.orders-summary {
  background-color: white;
  padding: 10px;
  border-radius: 4px;
  text-align: center;
}

.orders-summary p {
  margin: 5px 0;
  font-size: 0.85rem;
  color: #666;
}

/* Range slider styling */
.range-slider {
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: #ddd;
  outline: none;
  -webkit-appearance: none;
  appearance: none;
  margin: 8px 0;
}

.range-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #3498db;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.range-slider::-moz-range-thumb {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #3498db;
  cursor: pointer;
  border: none;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.range-labels {
  display: flex;
  justify-content: space-between;
  font-size: 0.75rem;
  color: #666;
  margin-top: -5px;
}

/* Analytics locations specific styles */
.analytics-category-filter {
  margin-bottom: 15px;
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 4px;
}

.analytics-category-filter label {
  display: block;
  margin-bottom: 5px;
  font-size: 0.85rem;
  font-weight: bold;
  color: #555;
}

.analytics-category-filter select {
  width: 100%;
  padding: 6px 8px;
  border: 1px solid #ddd;
  border-radius: 3px;
  font-size: 0.85rem;
}

.analytics-controls-panel {
  margin-bottom: 15px;
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 4px;
}

.analytics-controls-panel h4 {
  margin: 0 0 15px 0;
  color: #2c3e50;
  font-size: 1rem;
}

.analytics-control {
  margin-bottom: 12px;
}

.analytics-control label {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
  color: #555;
  cursor: pointer;
}

.analytics-control input[type="checkbox"] {
  margin-right: 8px;
}

.analytics-control select {
  width: 100%;
  padding: 6px 8px;
  border: 1px solid #ddd;
  border-radius: 3px;
  font-size: 0.85rem;
  margin-top: 5px;
}

.analytics-stats-summary {
  background-color: white;
  padding: 10px;
  border-radius: 4px;
  margin-bottom: 15px;
}

.analytics-stats-summary h4 {
  margin: 0 0 10px 0;
  color: #2c3e50;
  font-size: 1rem;
}

.analytics-stats-summary p {
  margin: 3px 0;
  font-size: 0.85rem;
  color: #666;
}

.category-breakdown {
  margin-top: 8px;
}

.category-item {
  font-size: 0.8rem;
  color: #666;
  margin: 2px 0;
}

.analytics-locations-list {
  max-height: 300px;
  overflow-y: auto;
}

.load-more-info {
  padding: 10px;
  text-align: center;
  font-size: 0.8rem;
  color: #666;
  font-style: italic;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .analytics-left-panel {
    width: 100%;
    height: 40%;
    position: fixed;
    top: auto;
    bottom: 0;
  }
  
  .analytics-right-panel {
    width: 100%;
    height: 40%;
    position: fixed;
    top: auto;
    bottom: 0;
  }
  
  .analytics-controls {
    left: 10px;
    top: 10px;
  }
  
  .back-button {
    left: 20px;
  }
}

@media (max-width: 576px) {
  .analytics-left-panel {
    width: 100%;
    height: 40%;
    position: fixed;
    top: auto;
    bottom: 0;
  }
  
  .analytics-controls {
    left: 10px;
    top: 10px;
  }
  
  .back-button {
    left: 20px;
  }
}

/* Analytics location popup styles */
:deep(.analytics-location-popup) {
  max-width: 300px;
  padding: 12px;
  font-family: inherit;
}

:deep(.analytics-location-popup h4) {
  margin: 0 0 12px 0;
  color: #2c3e50;
  font-size: 1.1rem;
  font-weight: 600;
  border-bottom: 1px solid #eee;
  padding-bottom: 8px;
}

:deep(.analytics-location-popup .location-details) {
  margin-bottom: 15px;
}

:deep(.analytics-location-popup .location-details p) {
  margin: 6px 0;
  font-size: 0.9rem;
  line-height: 1.4;
}

:deep(.analytics-location-popup .location-details strong) {
  font-weight: 600;
  color: #2c3e50;
}

:deep(.analytics-location-popup .popup-actions) {
  text-align: center;
  border-top: 1px solid #eee;
  padding-top: 12px;
}

:deep(.analytics-location-popup .add-location-btn) {
  background: #007bff;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

:deep(.analytics-location-popup .add-location-btn:hover) {
  background: #0056b3;
  transform: translateY(-1px);
}

:deep(.analytics-location-popup .add-location-btn:disabled) {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

:deep(.analytics-location-popup .add-location-btn i) {
  font-size: 0.8rem;
}

/* Analytics hexagon popup styles */
:deep(.analytics-hexagon-popup) {
  max-width: 280px;
  padding: 12px;
  font-family: inherit;
}

:deep(.analytics-hexagon-popup h4) {
  margin: 0 0 12px 0;
  color: #2c3e50;
  font-size: 1.1rem;
  font-weight: 600;
  border-bottom: 1px solid #eee;
  padding-bottom: 8px;
}

:deep(.analytics-hexagon-popup .hexagon-details) {
  margin-bottom: 15px;
}

:deep(.analytics-hexagon-popup .hexagon-details p) {
  margin: 6px 0;
  font-size: 0.9rem;
  line-height: 1.4;
}

:deep(.analytics-hexagon-popup .hexagon-details strong) {
  font-weight: 600;
  color: #2c3e50;
}

:deep(.analytics-hexagon-popup .popup-actions) {
  text-align: center;
  border-top: 1px solid #eee;
  padding-top: 12px;
}

:deep(.analytics-hexagon-popup .view-locations-btn) {
  background: #17a2b8;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

:deep(.analytics-hexagon-popup .view-locations-btn:hover) {
  background: #138496;
  transform: translateY(-1px);
}

:deep(.analytics-hexagon-popup .view-locations-btn i) {
  font-size: 0.8rem;
}

/* Notification styles */
.analytics-notification {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 10000;
  min-width: 300px;
  max-width: 400px;
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transform: translateX(420px);
  transition: transform 0.3s ease;
  font-family: inherit;
  font-size: 0.9rem;
}

.analytics-notification.show {
  transform: translateX(0);
}

.analytics-notification.success {
  background: #d4edda;
  border: 1px solid #c3e6cb;
  color: #155724;
}

.analytics-notification.error {
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  color: #721c24;
}

.analytics-notification.info {
  background: #d1ecf1;
  border: 1px solid #bee5eb;
  color: #0c5460;
}

.analytics-notification .notification-content {
  display: flex;
  align-items: center;
  gap: 10px;
}

.analytics-notification .notification-content i {
  font-size: 1.1rem;
  flex-shrink: 0;
}

.analytics-notification .notification-content span {
  line-height: 1.4;
}
</style>
